import { tokens } from '@gripp/shared-ui';
import { TextStyle } from 'react-native';

/**
 * The printed sheet is plain DOM + CSS rather than Crop Circle components: it
 * needs `break-inside`, `@page` and physical print units, none of which exist
 * in react-native-web's style model. Colours are fixed black-on-white so the
 * sheet is identical whichever theme the app is in — a role token would flip
 * with the theme and print a black rectangle in dark mode.
 *
 * Typography and spacing still come from Crop Circle tokens; only the colour
 * scale is local to this file.
 */

const INK = '#000000';
const INK_SOFT = '#4A4A4A';
const RULE = '#8A8A8A';
const RULE_LIGHT = '#BFBFBF';
const PAPER = '#FFFFFF';

/** Tall enough that an adult can write a legible line inside it. */
const WRITE_LINE_HEIGHT = 28;

/**
 * The band the repeated footer occupies when printing. Chrome paints a fixed
 * element over the flow rather than reserving room for it, so every question
 * carries this much blank tail for the footer to land in.
 */
const FOOTER_BAND_HEIGHT = 22;

export const PRINT_SHEET_CLASS = 'rps';

type FontToken = {
  fontFamily: string;
  fontSize: number;
  fontWeight?: TextStyle['fontWeight'];
  lineHeight: number;
};

const font = (token: FontToken): string =>
  [
    `font-family: '${token.fontFamily}', system-ui, -apple-system, sans-serif`,
    `font-size: ${token.fontSize}px`,
    `font-weight: ${token.fontWeight ?? '400'}`,
    `line-height: ${token.lineHeight}px`,
  ].join('; ');

/**
 * Emitted as a `<style>` element *inside* the sheet node, not into the document
 * head. react-to-print clones the node's markup into its own iframe, so keeping
 * the rules inside the node is what makes the printed output independent of the
 * app's stylesheet.
 */
export const printSheetCss = `
@page {
  size: letter;
  margin: 0.55in 0.6in 0.7in;
}

.${PRINT_SHEET_CLASS} {
  color-scheme: light;
  background: ${PAPER};
  color: ${INK};
  box-sizing: border-box;
  width: 8.5in;
  min-height: 11in;
  padding: 0.6in 0.65in 0.8in;
  ${font(tokens.typography.body.small)};
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.${PRINT_SHEET_CLASS} * {
  box-sizing: border-box;
}

.${PRINT_SHEET_CLASS}__title {
  ${font(tokens.typography.title.largeEmphasised)};
  margin: 0;
  color: ${INK};
}

.${PRINT_SHEET_CLASS}__routineInstructions {
  ${font(tokens.typography.body.small)};
  margin: ${tokens.spacing.sm}px 0 0;
  color: ${INK_SOFT};
  white-space: pre-wrap;
}

.${PRINT_SHEET_CLASS}__header {
  border-bottom: 2px solid ${INK};
  padding-bottom: ${tokens.spacing.md}px;
  margin-bottom: ${tokens.spacing['2xl']}px;
}

.${PRINT_SHEET_CLASS}__logo {
  display: block;
  flex-shrink: 0;
}


.${PRINT_SHEET_CLASS}__legend {
  ${font(tokens.typography.label.small)};
  color: ${INK_SOFT};
  margin-top: ${tokens.spacing.md}px;
  display: none;
}

.${PRINT_SHEET_CLASS}__legend--shown,
.${PRINT_SHEET_CLASS}:has(.${PRINT_SHEET_CLASS}__required) .${PRINT_SHEET_CLASS}__legend {
  display: block;
}

.${PRINT_SHEET_CLASS}__section {
  margin-bottom: ${tokens.spacing.lg}px;
}

.${PRINT_SHEET_CLASS}__sectionTitle {
  ${font(tokens.typography.title.smallEmphasised)};
  margin: 0 0 ${tokens.spacing.sm}px;
  padding-bottom: ${tokens.spacing.xs}px;
  border-bottom: 1px solid ${RULE_LIGHT};
  break-after: avoid;
  page-break-after: avoid;
}

.${PRINT_SHEET_CLASS}__sectionInstructions {
  ${font(tokens.typography.body.small)};
  color: ${INK_SOFT};
  margin: 0 0 ${tokens.spacing.md}px;
  white-space: pre-wrap;
}

.${PRINT_SHEET_CLASS}__question {
  break-inside: avoid;
  page-break-inside: avoid;
  /* Empty tail rather than a margin: it travels with the block, so the only
     thing the repeated print footer can ever overlap is this blank strip. */
  padding-bottom: ${FOOTER_BAND_HEIGHT}px;
}

.${PRINT_SHEET_CLASS}__questionHead {
  display: flex;
  gap: ${tokens.spacing.sm}px;
  align-items: baseline;
  margin-bottom: ${tokens.spacing.sm}px;
}

.${PRINT_SHEET_CLASS}__questionNumber {
  ${font(tokens.typography.body.smallEmphasised)};
  color: ${INK};
  min-width: 20px;
}

.${PRINT_SHEET_CLASS}__questionText {
  ${font(tokens.typography.body.smallEmphasised)};
  color: ${INK};
  white-space: pre-wrap;
  flex: 1 1 auto;
}

.${PRINT_SHEET_CLASS}__required {
  ${font(tokens.typography.body.smallEmphasised)};
  color: ${INK};
}

.${PRINT_SHEET_CLASS}__answer {
  padding-left: 28px;
}

.${PRINT_SHEET_CLASS}__rule {
  border-bottom: 1px solid ${RULE};
  height: ${WRITE_LINE_HEIGHT}px;
}

.${PRINT_SHEET_CLASS}__ruleStack > .${PRINT_SHEET_CLASS}__rule + .${PRINT_SHEET_CLASS}__rule {
  margin-top: ${tokens.spacing.xs}px;
}

.${PRINT_SHEET_CLASS}__option {
  display: flex;
  align-items: flex-start;
  gap: ${tokens.spacing.sm}px;
  min-height: 20px;
  padding: 1px 0;
  break-inside: avoid;
  page-break-inside: avoid;
}

.${PRINT_SHEET_CLASS}__optionText {
  ${font(tokens.typography.body.small)};
  color: ${INK};
  overflow-wrap: anywhere;
}

.${PRINT_SHEET_CLASS}__checkGlyph {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border: 1px solid ${INK};
}

.${PRINT_SHEET_CLASS}__radioGlyph {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border: 1px solid ${INK};
  border-radius: 50%;
}

.${PRINT_SHEET_CLASS}__inlineFields {
  display: flex;
  gap: ${tokens.spacing.lg}px;
  align-items: flex-end;
}

.${PRINT_SHEET_CLASS}__fieldLabel {
  ${font(tokens.typography.label.small)};
  color: ${INK_SOFT};
  margin-top: ${tokens.spacing.xxs}px;
}

.${PRINT_SHEET_CLASS}__slot {
  display: flex;
  flex-direction: column;
}

.${PRINT_SHEET_CLASS}__signature {
  border: 1px solid ${INK};
  height: 0.95in;
}

.${PRINT_SHEET_CLASS}__mediaBox {
  border: 1px solid ${RULE};
  height: 2in;
}

.${PRINT_SHEET_CLASS}__table {
  width: 100%;
  border-collapse: collapse;
}

.${PRINT_SHEET_CLASS}__table th {
  ${font(tokens.typography.label.smallEmphasised)};
  color: ${INK_SOFT};
  text-align: left;
  padding-bottom: ${tokens.spacing.xxs}px;
  border-bottom: 1px solid ${RULE_LIGHT};
}

.${PRINT_SHEET_CLASS}__table td {
  border-bottom: 1px solid ${RULE};
  height: ${WRITE_LINE_HEIGHT}px;
}

/* Column rules, so a writer can see where Barcode ends and Qty begins. */
.${PRINT_SHEET_CLASS}__table th + th,
.${PRINT_SHEET_CLASS}__table td + td {
  border-left: 1px solid ${RULE_LIGHT};
  padding-left: ${tokens.spacing.sm}px;
}

.${PRINT_SHEET_CLASS}__nestedTitle {
  ${font(tokens.typography.label.smallEmphasised)};
  color: ${INK_SOFT};
  margin-bottom: ${tokens.spacing.xxs}px;
}

.${PRINT_SHEET_CLASS}__questionMedia {
  max-width: 1.5in;
  max-height: 1.5in;
  margin-bottom: ${tokens.spacing.sm}px;
  display: block;
}

.${PRINT_SHEET_CLASS}__footer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${tokens.spacing.lg}px;
  border-top: 1px solid ${RULE_LIGHT};
  padding-top: ${tokens.spacing.xs}px;
  margin-top: ${tokens.spacing['2xl']}px;
  background: ${PAPER};
  ${font(tokens.typography.label.small)};
  color: ${INK_SOFT};
}

.${PRINT_SHEET_CLASS}__footerTitle,
.${PRINT_SHEET_CLASS}__footerAsset {
  /* min-width lets a grid column shrink below its content, which is what makes
     the ellipsis engage instead of widening the column and shifting the mark. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.${PRINT_SHEET_CLASS}__footerAsset {
  text-align: right;
}

@media print {
  .${PRINT_SHEET_CLASS} {
    width: auto;
    min-height: 0;
    padding: 0;
    box-shadow: none;
  }

  /* Chrome repeats a fixed-position element on every printed page. It is the
     only repeating-footer mechanism the engine supports: page margin boxes,
     which would also carry page numbers, are unimplemented. */
  .${PRINT_SHEET_CLASS}__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin-top: 0;
  }
}
`;
